import React, { FC } from 'react'
import { Box, Image } from '@moonpig/launchpad-components'
import { IconSystemTickFilled } from '@moonpig/launchpad-assets'
import { styled, css } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { colorValue } from '@moonpig/launchpad-theme'

const CardThumbnailLabel = styled.label<{ $isSelected: boolean }>`
  ${s({
    borderRadius: 2,
    width: '100%',
    padding: 0,
    position: 'relative',
  })}
  display: block;
  cursor: pointer;

  ${({ $isSelected }) =>
    $isSelected
      ? css`
          outline: 4px solid;
          outline-color: ${colorValue('colorInteractionSelectedState')};

          &:has(input:focus-visible) {
            box-shadow:
              0 0 0 6px ${colorValue('colorBackground01')},
              0 0 0 8px ${colorValue('colorInteractionFocusIndicator')};
          }
        `
      : css`
          &:hover,
          &:has(input:focus-visible) {
            outline: 4px solid;
            outline-color: ${colorValue('colorInteractionSelectedStateHover')};
          }
        `}
`

const VisuallyHiddenRadio = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`

const TickIcon = styled(IconSystemTickFilled)`
  ${s({
    color: 'colorInteractionSelectedState',
    bgcolor: 'colorBackground01',
    borderRadius: '50%',
    width: '24px',
    height: '24px',
  })}
`

const CardImage = styled(Image)`
  ${s({
    width: '100%',
    height: '100%',
    borderRadius: 1,
  })}
`

type CardThumbnailProps = {
  id: string
  name: string
  title: string
  imageUrl: string
  isSelected: boolean
  onSelect: (id: string) => void
  ariaLabel: string
}

export const CardThumbnail: FC<CardThumbnailProps> = ({
  id,
  name,
  title,
  imageUrl,
  isSelected,
  onSelect,
  ariaLabel,
}) => (
  <CardThumbnailLabel
    $isSelected={isSelected}
    data-testid={`mp-select-card-thumbnail-${id}`}
  >
    <VisuallyHiddenRadio
      type="radio"
      name={name}
      value={id}
      checked={isSelected}
      onChange={() => onSelect(id)}
      aria-label={ariaLabel}
    />
    <CardImage alt={title} src={imageUrl} />
    {isSelected && (
      <Box position="absolute" top={4} right={4}>
        <TickIcon />
      </Box>
    )}
  </CardThumbnailLabel>
)
